import type { UnitConversion } from '@familychef/shared';
import { INGREDIENT_UNITS, INGREDIENT_UNIT_LABELS, UNIT_TYPES } from '@familychef/shared';
import { Edit, Plus, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  useConversions,
  useUpsertConversion,
  useDeleteConversion,
  type ConversionRow,
} from '../../hooks/useConversions';

import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';

export function ConversionsPage() {
  const { data: rows = [], isLoading } = useConversions();
  const upsert = useUpsertConversion();
  const deleteConversion = useDeleteConversion();
  const [editingRow, setEditingRow] = useState<ConversionRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConversionRow | null>(null);
  const [newRow, setNewRow] = useState<Partial<ConversionRow>>({
    from_unit: 'g',
    to_unit: 'kg',
    factor: 0.001,
    unit_type: 'weight',
  });
  const columns = [
    {
      key: 'from_unit',
      header: 'De',
      cell: (row: ConversionRow) =>
        editingRow?.from_unit === row.from_unit && editingRow?.to_unit === row.to_unit ? (
          <select
            value={editingRow.from_unit}
            onChange={(e) =>
              setEditingRow({
                ...editingRow,
                from_unit: e.target.value as UnitConversion['from_unit'],
              })
            }
            className="h-8 rounded border border-slate-200 px-2 text-sm focus:border-green-500 focus:outline-none"
          >
            {INGREDIENT_UNITS.map((u) => (
              <option key={u} value={u}>
                {INGREDIENT_UNIT_LABELS[u]?.fr ?? u}
              </option>
            ))}
          </select>
        ) : (
          <span className="badge bg-slate-100 text-slate-700">
            {INGREDIENT_UNIT_LABELS[row.from_unit]?.fr ?? row.from_unit}
          </span>
        ),
    },
    {
      key: 'to_unit',
      header: 'Vers',
      cell: (row: ConversionRow) =>
        editingRow?.from_unit === row.from_unit && editingRow?.to_unit === row.to_unit ? (
          <select
            value={editingRow.to_unit}
            onChange={(e) =>
              setEditingRow({
                ...editingRow,
                to_unit: e.target.value as UnitConversion['to_unit'],
              })
            }
            className="h-8 rounded border border-slate-200 px-2 text-sm focus:border-green-500 focus:outline-none"
          >
            {INGREDIENT_UNITS.map((u) => (
              <option key={u} value={u}>
                {INGREDIENT_UNIT_LABELS[u]?.fr ?? u}
              </option>
            ))}
          </select>
        ) : (
          <span className="badge bg-blue-100 text-blue-700">
            {INGREDIENT_UNIT_LABELS[row.to_unit]?.fr ?? row.to_unit}
          </span>
        ),
    },
    {
      key: 'factor',
      header: 'Facteur',
      className: 'hidden sm:table-cell',
      cell: (row: ConversionRow) =>
        editingRow?.from_unit === row.from_unit && editingRow?.to_unit === row.to_unit ? (
          <input
            type="number"
            step="0.000001"
            value={editingRow.factor}
            onChange={(e) => setEditingRow({ ...editingRow, factor: parseFloat(e.target.value) })}
            className="flex h-8 w-28 rounded border border-slate-200 px-2 text-sm focus:border-green-500 focus:outline-none"
          />
        ) : (
          <span className="text-slate-700">{row.factor}</span>
        ),
    },
    {
      key: 'type',
      header: 'Type',
      className: 'hidden sm:table-cell',
      cell: (row: ConversionRow) => (
        <span className="badge bg-purple-100 text-purple-700">{row.unit_type}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-20',
      cell: (row: ConversionRow) => (
        <div className="flex justify-end gap-1">
          {editingRow?.from_unit === row.from_unit && editingRow?.to_unit === row.to_unit ? (
            <>
              <button
                onClick={() => {
                  upsert.mutate(editingRow);
                  setEditingRow(null);
                }}
                className="rounded-md p-1.5 text-green-600 hover:bg-green-50"
              >
                <Save size={15} />
              </button>
              <button
                onClick={() => setEditingRow(null)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditingRow({ ...row })}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Edit size={15} />
              </button>
              <button
                onClick={() => setDeleteTarget(row)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <AppShell>
      <TopBar title="Conversions d'unités" subtitle={`${rows.length} conversions définies`} />
      <div className="p-4 md:p-6">
        <PageHeader
          title="Table de conversions"
          subtitle="Facteurs de conversion entre unités de mesure"
        />

        <div className="card overflow-hidden !p-0">
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowKey={(row) => `${row.from_unit}-${row.to_unit}`}
            emptyMessage="Aucune conversion trouvée"
          />

          {/* Formulaire nouvelle conversion */}
          <div className="border-t border-slate-200 p-4">
            <p className="mb-3 text-sm font-medium text-slate-700">Ajouter une conversion</p>
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-end gap-3 sm:gap-2">
              <div className="w-full sm:w-auto">
                <label className="mb-1 block text-xs text-slate-500">De</label>
                <select
                  value={newRow.from_unit}
                  onChange={(e) =>
                    setNewRow((p) => ({
                      ...p,
                      from_unit: e.target.value as UnitConversion['from_unit'],
                    }))
                  }
                  className="h-9 rounded-lg border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
                >
                  {INGREDIENT_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {INGREDIENT_UNIT_LABELS[u]?.fr ?? u}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-auto">
                <label className="mb-1 block text-xs text-slate-500">Vers</label>
                <select
                  value={newRow.to_unit}
                  onChange={(e) =>
                    setNewRow((p) => ({
                      ...p,
                      to_unit: e.target.value as UnitConversion['to_unit'],
                    }))
                  }
                  className="h-9 w-full sm:w-auto rounded-lg border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
                >
                  {INGREDIENT_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {INGREDIENT_UNIT_LABELS[u]?.fr ?? u}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-auto">
                <label className="mb-1 block text-xs text-slate-500">Facteur</label>
                <input
                  type="number"
                  step="0.000001"
                  value={newRow.factor ?? ''}
                  onChange={(e) => setNewRow((p) => ({ ...p, factor: parseFloat(e.target.value) }))}
                  className="h-9 w-full sm:w-28 rounded-lg border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
                  placeholder="0.001"
                />
              </div>
              <div className="w-full sm:w-auto">
                <label className="mb-1 block text-xs text-slate-500">Type</label>
                <select
                  value={newRow.unit_type}
                  onChange={(e) =>
                    setNewRow((p) => ({
                      ...p,
                      unit_type: e.target.value as UnitConversion['unit_type'],
                    }))
                  }
                  className="h-9 w-full sm:w-auto rounded-lg border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
                >
                  {UNIT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => {
                  if (newRow.from_unit && newRow.to_unit && newRow.factor && newRow.unit_type) {
                    upsert.mutate(newRow as ConversionRow);
                    setNewRow({
                      from_unit: 'g',
                      to_unit: 'kg',
                      factor: 0.001,
                      unit_type: 'weight',
                    });
                  }
                }}
                className="mt-2 sm:mt-0 flex h-9 w-full sm:w-auto items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <Plus size={15} /> Ajouter
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Supprimer la conversion"
        message={`Supprimer la conversion ${deleteTarget?.from_unit} → ${deleteTarget?.to_unit} ?`}
        confirmLabel="Supprimer"
        isDestructive
        isLoading={deleteConversion.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteConversion.mutate(
            { from_unit: deleteTarget.from_unit, to_unit: deleteTarget.to_unit },
            { onSuccess: () => setDeleteTarget(null) },
          );
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppShell>
  );
}
